import _ from "lodash";
import React, { memo, useState } from "react";
import UIModal from "UIComponents/UIModal";
import {
  Button,
  IconButton,
  Alert,
  Tooltip,
  Checkbox,
} from "@toddle-edu/ds-web";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import { colors } from "Constants";
import classNames from "classnames";
import classes from "./DuplicateTemplateModal.scss";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";
import { Trans } from "react-i18next";

const components = { b: <b /> };

const styles = {
  tooltipStyle: {
    maxWidth: "304px",
  },
};

const DuplicateTemplateModal = memo(
  ({
    t,
    modalState,
    subjectName,
    templateName,
    closeModal,
    getTooltip,
    grades,
    usedCourses,
    gradesWithNoCategorySet,
    selectedGrades,
    setSelectedGrades,
    rootNodes,
    onClickDuplicate: _onClickDuplicate,
    terms,
    selectedTerms,
    setSelectedTerms,
    usedTerms,
    termsWithNoCategorySet,
    parentType,
    canOverrideExistingTemplates,
  }) => {
    const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);

    const isAllGradesSelected =
      _.size(selectedGrades) === _.size(gradesWithNoCategorySet);

    const isAllTermsSelected =
      _.size(selectedTerms) === _.size(termsWithNoCategorySet);

    let isDuplicateBtnDisabled =
      modalState === "DUPLICATE_TO_OTHER_GRADES" && _.isEmpty(selectedGrades);

    if (
      modalState === "DUPLICATE_TO_OTHER_GRADES" &&
      canOverrideExistingTemplates
    ) {
      isDuplicateBtnDisabled = !isCheckboxChecked || isDuplicateBtnDisabled;
    }

    const onClickAllCheckbox = () => {
      if (_.isEmpty(selectedGrades)) {
        setSelectedGrades(gradesWithNoCategorySet);
      } else {
        setSelectedGrades([]);
      }
    };

    const onClickAllTermsCheckbox = () => {
      if (_.isEmpty(selectedTerms)) {
        setSelectedTerms(termsWithNoCategorySet);
      } else {
        setSelectedTerms([]);
      }
    };

    const onClickDuplicate = () => {
      switch (modalState) {
        case "DUPLICATE_TO_ALL_SUBJECTS":
          _onClickDuplicate(grades);
          break;
        case "DUPLICATE_TO_OTHER_GRADES":
          _onClickDuplicate(selectedGrades);
          break;
        case "DUPLICATE_TO_OTHER_TERMS":
          _onClickDuplicate(selectedTerms);
          break;
      }
    };

    const othersSubjectsCount = _.size(
      _.filter(rootNodes, node => {
        return node?.type === "OTHERS";
      })
    );

    const getHeaderLabel = () => {
      switch (modalState) {
        case "DUPLICATE_TO_ALL_SUBJECTS":
          return t("academicSetup:duplicate_label_to_all_subjects_of_term", {
            label: templateName,
          });
        case "DUPLICATE_TO_OTHER_GRADES":
          if (_.isEmpty(templateName)) {
            return t("academicSetup:duplicate_to_other_grades_of_this_subject");
          }
          return t(
            "academicSetup:duplicate_to_other_grades_of_this_subject_label",
            { label: templateName }
          );
        case "DUPLICATE_TO_OTHER_TERMS":
          return t(
            "schoolSetup:duplicate_to_other_terms_of_this_subject_label",
            { label: templateName }
          );
      }
    };

    const getSubText = () => {
      if (othersSubjectsCount > 0) {
        return (
          <Trans
            i18nKey="academicSetup:duplicate_label_to_all_subjects_excl_others_of_term_description"
            components={components}
            values={{ label: templateName }}
          >
            You are duplicating ‘{{ label: templateName }}’ to all subjects of
            this grading period
            <b>
              (excluding Theory of Knowledge and subjects from the ‘Others’
              subject group)
            </b>
            . You can edit them individually from their respective subject page.
          </Trans>
        );
      }

      return (
        <Trans
          i18nKey="academicSetup:duplicate_label_to_all_subjects_of_term_description"
          components={components}
          values={{ label: templateName }}
        >
          You are duplicating ‘{{ label: templateName }}’ to all subjects of
          this grading period <b>(excluding Theory of Knowledge)</b>. You can
          edit them individually from their respective subject page.
        </Trans>
      );
    };

    const onCheckboxClick = () => {
      setIsCheckboxChecked(!isCheckboxChecked);
    };

    return (
      <UIModal isOpen={true} modalContent={classes.modalContent}>
        <div className={classes.modalHeaderContainer}>
          <div className="heading-4">{getHeaderLabel()}</div>
          <IconButton
            icon={<CloseOutlined />}
            variant="plain"
            onClick={closeModal}
          />
        </div>
        <div className={classes.modalBodyContainer}>
          {modalState === "DUPLICATE_TO_ALL_SUBJECTS" ? (
            <React.Fragment>
              <div className="text-body-1">{getSubText()}</div>
              <Alert
                type={"warning"}
                message={_.concat(
                  t("academicSetup:this_template_would_not_override"),
                  " ",
                  <Tooltip
                    tooltip={getTooltip()}
                    placement="left"
                    overlayInnerStyle={styles.tooltipStyle}
                  >
                    <span className={classes.tooltipText}>
                      {_.toLower(t("academicSetup:existing_category_sets"))}
                    </span>
                  </Tooltip>,
                  " ",
                  t("academicSetup:in_this_term")
                )}
                isClosable={false}
              />
            </React.Fragment>
          ) : modalState === "DUPLICATE_TO_OTHER_GRADES" ? (
            <React.Fragment>
              <div className="text-body-1">
                {t(
                  parentType === "GRADE_SCALES"
                    ? "academicSetup:which_other_grades_of_label_would_you_like_to_duplicate_this_grade_boundary_to"
                    : "academicSetup:select_which_grades_you_would_like_to_duplicate_label",
                  { label: subjectName }
                )}
              </div>
              <div className={classes.gradesContainer}>
                <div
                  className={classes.gradeNameContainer}
                  onClick={onClickAllCheckbox}
                >
                  <Checkbox
                    isIndeterminate={
                      !_.isEmpty(selectedGrades) && !isAllGradesSelected
                    }
                    isChecked={isAllGradesSelected}
                  />
                  <div className="text-body-1">{t("common:all_grades")}</div>
                </div>
                {_.map(grades, grade => {
                  const gradeId = _.get(grade, "id");
                  const isDisabled = _.includes(usedCourses, gradeId);
                  const gradeName = _.concat(
                    _.get(grade, "name", ""),
                    isDisabled
                      ? ` (${t("academicSetup:template_already_exists")})`
                      : ""
                  );

                  const gradeNameContainerClass = classNames({
                    [classes.gradeNameContainer]: true,
                    [classes.textDisabled]: isDisabled,
                  });

                  const isGradeSelected = _.includes(selectedGrades, gradeId);

                  const onClickGradeCheckbox = () => {
                    if (isDisabled) return;
                    if (!isGradeSelected) {
                      setSelectedGrades([...selectedGrades, gradeId]);
                    } else {
                      setSelectedGrades(
                        _.filter(selectedGrades, grade => grade !== gradeId)
                      );
                    }
                  };

                  return (
                    <div
                      className={gradeNameContainerClass}
                      onClick={onClickGradeCheckbox}
                    >
                      <Checkbox
                        disabled={isDisabled}
                        isChecked={isGradeSelected}
                      />
                      <div className="text-body-1">{gradeName}</div>
                    </div>
                  );
                })}
              </div>
              {canOverrideExistingTemplates ? (
                <div
                  className={classes.alertContainer}
                  onClick={onCheckboxClick}
                >
                  <Checkbox
                    isChecked={isCheckboxChecked}
                    color={{ background: colors.interactiveOneDefault }}
                  />
                  <span className="text-label-2">
                    <Trans
                      i18nKey="academicSetup:reset_grade_boundary_confirmation"
                      components={components}
                    >
                      I understand that this action would cause the
                      <b>
                        overall grade to be recalculated in the gradebook and in
                        unlocked progress reports.
                      </b>
                    </Trans>
                  </span>
                </div>
              ) : null}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <div className="text-body-1">
                {t(
                  "schoolSetup:select_which_terms_you_would_like_to_duplicate_label",
                  { label: subjectName }
                )}
              </div>
              <div className={classes.gradesContainer}>
                <div
                  className={classes.gradeNameContainer}
                  onClick={onClickAllTermsCheckbox}
                >
                  <Checkbox
                    isIndeterminate={
                      !_.isEmpty(selectedTerms) && !isAllTermsSelected
                    }
                    isChecked={isAllTermsSelected}
                  />
                  <div className="text-body-1">
                    {t("common:all_label", { label: t("common:term_plural") })}
                  </div>
                </div>
                {_.map(terms, term => {
                  const termID = _.get(term, "id");
                  const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
                    term
                  );
                  const isDisabled =
                    _.includes(usedTerms, termID) || !isGradingPeriodUnlocked;
                  const termName = _.concat(
                    _.get(term, "label", ""),
                    isDisabled
                      ? ` (${t("academicSetup:template_already_exists")})`
                      : ""
                  );

                  const gradeNameContainerClass = classNames({
                    [classes.gradeNameContainer]: true,
                    [classes.textDisabled]: isDisabled,
                  });

                  const isTermSelected = _.includes(selectedTerms, termID);

                  const onClicktermsCheckbox = () => {
                    if (isDisabled) return;
                    if (!isTermSelected) {
                      setSelectedTerms([...selectedTerms, termID]);
                    } else {
                      setSelectedTerms(
                        _.filter(selectedTerms, grade => grade !== termID)
                      );
                    }
                  };

                  return (
                    <ConditionalWrapper
                      condition={!isGradingPeriodUnlocked}
                      wrapper={
                        <Tooltip
                          tooltip={t(
                            "academicSetup:you_cannot_duplicate_categories_since_locked"
                          )}
                        />
                      }
                      type={"wrapperAsElement"}
                    >
                      <div
                        className={gradeNameContainerClass}
                        onClick={onClicktermsCheckbox}
                      >
                        <Checkbox
                          disabled={isDisabled}
                          isChecked={isTermSelected}
                        />

                        <div className="text-body-1">{termName}</div>
                      </div>
                    </ConditionalWrapper>
                  );
                })}
              </div>
            </React.Fragment>
          )}
        </div>
        <div className={classes.modalFooterContainer}>
          <Button size="large" variant="outlined-subtle" onClick={closeModal}>
            {t("common:cancel")}
          </Button>
          <Button
            size="large"
            variant="progressive"
            onClick={onClickDuplicate}
            disabled={isDuplicateBtnDisabled}
          >
            {t("common:duplicate")}
          </Button>
        </div>
      </UIModal>
    );
  }
);

DuplicateTemplateModal.displayName = "DuplicateTemplateModal";

export default DuplicateTemplateModal;
